<template>
  <div class="pass-back status-bar-safe-with-offset">
      <BackButton/>
      </div>
    <div class="pass-wrapper">
      <!-- 标题 -->
      <h1 class="pass-title">{{ $t('changePassword.title') }}</h1>
      
      <!-- 当前密码 -->
      <PasswordInput
        v-model="formData.currentPassword"
        :placeholder="$t('changePassword.currentPassword')"
        :error="!!currentPasswordError"
        @blur="validateCurrentPassword"
        @clear="clearErrors"
      />

      <!-- 新密码 -->
      <PasswordInput
        v-model="formData.newPassword"
        :placeholder="$t('changePassword.newPassword')"
        :error="!!newPasswordError"
        @blur="validateNewPassword"
        @clear="clearErrors"
      />
      <!-- 确认新密码 -->
      <PasswordInput
        v-model="formData.confirmPassword"
        :placeholder="$t('changePassword.confirmPassword')"
        :error="!!confirmPasswordError"
        @blur="validateConfirmPassword"
        @clear="clearErrors"
      />
      
      <!-- 提交按钮 -->
      <button 
        class="pass-btn" 
        :disabled="loading || !isFormValid" 
        @click="handleSubmit"
      >
        {{ loading ? $t('common.loading') : $t('btn.confirm') }}
      </button>
    </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue';
import { useRouter } from 'vue-router';
import { toast } from 'd3components/src/utils/tools';
import PasswordInput from '../passwordInput.vue';
import { useI18n } from 'vue-i18n';
import BackButton from '@/layout/components/backbutton.vue';

import {
  ApiUser
} from 'd3components';
import { encodePass } from 'd3components/src/utils/tools';
const { updateUserPass }  = ApiUser;
const router = useRouter();
const { t } = useI18n({ useScope: 'global' });

// 加载状态
const loading = ref(false);
// 错误状态
const currentPasswordError = ref('');
const newPasswordError = ref('');
const confirmPasswordError = ref('');

// 表单数据
const formData = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
});

// 表单整体验证
const isFormValid = computed(() => {
  return formData.currentPassword && formData.newPassword && formData.confirmPassword && 
         !currentPasswordError.value && !newPasswordError.value && !confirmPasswordError.value;
});

// 清除错误信息
const clearErrors = () => {
  currentPasswordError.value = '';
  newPasswordError.value = '';
  confirmPasswordError.value = '';
};

// 表单验证规则
const validateCurrentPassword = () => {
  if (!formData.currentPassword) {
    currentPasswordError.value = t('changePassword.currentPassword');
    console.log('当前密码不能为空');
    return false;
  }
  if (formData.currentPassword.length < 1) {
    currentPasswordError.value = t('changePassword.currentPassword');
    console.log('当前密码不能为空');
    return false;
  }
  currentPasswordError.value = '';
  return true;
};

const validateNewPassword = () => {
  if (!formData.newPassword) {
    newPasswordError.value = t('changePassword.newPassword');
    console.log('请输入新密码');
    return false;
  }
  newPasswordError.value = '';
  return true;
};

const validateConfirmPassword = () => {
  if (!formData.confirmPassword) {
    confirmPasswordError.value = t('changePassword.confirmPassword');
    console.log('请确认新密码');
    toast(t('changePassword.confirmPassword'), 3);
    return false;
  }
  if (formData.confirmPassword !== formData.newPassword) {
    confirmPasswordError.value = t('common.passwordNotMatch');
    console.log('两次输入的密码不一致');
    toast(t('common.passwordNotMatch'), 3);
    return false;
  }
  confirmPasswordError.value = '';
  return true;
};

// 提交表单
const handleSubmit = async () => {
  try {
    // 表单验证
    if (!validateCurrentPassword() || !validateNewPassword() || !validateConfirmPassword()) {
      return;
    }

    loading.value = true;

    // 发送修改密码请求
    const par = {
      newPwd: encodePass(formData.newPassword), // 使用新密码进行加密
      oldPwd: encodePass(formData.currentPassword) // 使用旧密码进行加密
    };

    updateUserPass(par)
      .then(({ code, value }) => {
        if (code === 0) {
          toast(value, 1);
          console.log(value);
          // resetForm();
          router.push('/login');
        } else {
          toast(value, 3);
          console.log(value);
        }
      })
      .catch((e) => {
        toast(e, 3);
        console.log('修改密码失败:', e);
      })
      .finally(() => {
        loading.value = false;
      });

    // 重置表单
    // resetForm();
  } catch (error) {
    loading.value = false;
    toast(t('changePassword.updateFailed'), 3);
    console.error('修改密码失败:', error);
  }
};

// 重置表单
const resetForm = () => {
  formData.currentPassword = '';
  formData.newPassword = '';
  formData.confirmPassword = '';
  clearErrors();
};
</script>

<style lang="scss" scoped>
@use '../shared/auth-common.scss';

/* 修改密码页面容器 */
.pass-wrapper {
  @extend .auth-wrapper;
}

/* 修改密码页面标题 */
.pass-title {
  @extend .title;
  padding-top: 50px;
}
.needs-status-bar-adaptation .status-bar-safe-with-offset {
  padding-top: var(--header-safe-padding-top) !important;
}
.pass-back{
  display: flex;
  padding: 10px  15px;
}
/* 密码输入框样式现在由PasswordInput组件处理 */

/* 修改密码按钮 */
.pass-btn {
  // @extend .auth-btn;
   width: 80%;
  display: block;
  margin-left: auto;
  margin-right: auto;
  // padding: 1rem 1rem 1rem  0;
  line-height: 3.5rem;
  border: 2px solid transparent;
  border-radius: 2rem;
  box-sizing: border-box;
  font-size: 1rem;
  font-weight: bold;
  color: white;
  // background: linear-gradient(90deg, #00e0ff, #0066ff);
  background-color: var(--color-primary);
  z-index: 10;
  transition: all 0.3s;
  margin-bottom: 1rem;
}
.pass-btn :disabled {
  background: hsla( var(--color-primary),0.04);
  opacity: 0.6;
}

.pass-btn :not(:disabled):active {
  transform: scale(0.98);
}
/* 闪烁logo动画 */
.flashing-logo {
  animation: simple-flash 3s infinite;
}

@keyframes simple-flash {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}
</style>
